import React from "react";
import { ChevronDown } from "lucide-react";
import InfoTip from "./InfoTip.jsx";

// Stavební kameny dlaždic na Přehledu (CEO). Vzhled je odsouhlasený v mockupech
// a vychází z hlavičky stránky: tmavý pruh nahoře, pod ním skupiny oddělené
// světle fialovým pruhem a řádky s číslem vpravo.
//
// Obsah dlaždic se tímto nemění, mění se jen tvar. Každá dlaždice drží stejná
// čísla, stejné nápovědy a stejné pořadí jako dosud.

// Tmavý pruh nahoře, zmenšená hlavička stránky. Ikona vlevo, název, vpravo
// nepovinný stavový odznak (například Live).
//
// 12.9.2026: tohle je JEDINÁ tmavá hlavička karty v adminu. Dřív byly dvě:
// tahle na Přehledu (fáze 4b) a starší plochá v `KartaSeznam` (fáze 4a, dvacet
// čtyři karet v Operations) plus sedm ručních kopií. `KartaSeznam` teď kreslí
// tuhle, proto přibyl podtitulek, tón odznaku a rozbalování.
export function HlavickaDlazdice({
  ikona: Ikona,
  nazev,
  podtitulek,
  napoveda,
  odznak,
  odznakTon = "bily",
  odznakZivy = false,
  akce,
  sbalitelne = false,
  otevreno = true,
  onToggle,
}) {
  const tridaOdznaku = `inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1 text-xs font-semibold tabular-nums ${
    odznakTon === "zluty" ? "bg-brand-gold text-brand-dark"
      : odznakTon === "cerveny" ? "bg-rose-500/90 text-white"
      : "bg-white/15 text-white"
  }`;
  const vnitrek = (
    <>
      {napoveda ? (
        <InfoTip text={napoveda} varianta="ikona" ikona={Ikona} />
      ) : (
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/25 bg-white/15 sm:h-10 sm:w-10">
          {Ikona && <Ikona className="h-5 w-5" />}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <h2 className="truncate font-serif text-lg font-bold leading-tight sm:text-xl">{nazev}</h2>
        {podtitulek && <div className="truncate text-[11px] text-white/70">{podtitulek}</div>}
      </div>
      {odznak != null && odznak !== false && (
        <span className={tridaOdznaku}>
          {odznakZivy && <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />}
          {odznak}
        </span>
      )}
      {akce}
      {sbalitelne && (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!!otevreno}
          aria-label={otevreno ? "Sbalit" : "Rozbalit"}
          // 14. 9. 2026 (fáze 6): na telefonu 44 px, na počítači původních 36.
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white transition active:scale-95 sm:h-9 sm:w-9 sm:hover:bg-white/15"
        >
          <ChevronDown className={`h-5 w-5 transition-transform ${otevreno ? "" : "-rotate-90"}`} />
        </button>
      )}
    </>
  );
  const tridy = "w-full bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 py-3 text-white sm:px-5 sm:py-3.5";

  // 14.9.2026 (fáze 6a): sbalitelná lišta byla celá jedno <button>. Uvnitř ale
  // sedí nápověda i akce, které jsou taky tlačítka, a tlačítko v tlačítku není
  // platné HTML, konzole to hlásila na každé takové kartě. Teď je lišta <div>,
  // klepnutí kamkoli mimo ovládací prvek ji přepne, a šipka vpravo je skutečné
  // tlačítko, takže to jde ovládat i z klávesnice.
  const naKlik = sbalitelne
    ? (e) => {
        if (e.target.closest && e.target.closest("button, a, input, select, textarea, label")) return;
        onToggle?.();
      }
    : undefined;

  return (
    <div className={`${tridy} ${sbalitelne ? "cursor-pointer select-none text-left" : ""}`} onClick={naKlik}>
      <div className="flex items-center gap-3">{vnitrek}</div>
    </div>
  );
}

// Nadpis skupiny uvnitř dlaždice, pruh přes celou šířku.
export function SkupinaPrehledu({ ikona: Ikona, nazev, napoveda, akce }) {
  return (
    <div className="flex items-center gap-2 bg-brand-light px-4 py-2 sm:px-5">
      {Ikona && <Ikona className="h-3.5 w-3.5 shrink-0 text-brand-deep" />}
      <span className="text-xs font-bold uppercase tracking-wide text-brand-950">
        {nazev}
      </span>
      {napoveda && <InfoTip text={napoveda} />}
      {akce && <span className="ml-auto flex shrink-0 items-center">{akce}</span>}
    </div>
  );
}

// Ikonové tlačítko do tmavé hlavičky dlaždice.
export function AkceHlavicky({ ikona: Ikona, popis, onClick, disabled = false, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={popis}
      aria-label={children ? undefined : popis}
      className={`flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-white/20 bg-white/10 text-white transition active:scale-95 disabled:opacity-50 sm:hover:bg-white/20 ${
        children ? "h-11 px-2.5 text-xs font-semibold sm:h-9" : "h-11 w-11 sm:h-9 sm:w-9"
      }`}
    >
      {Ikona && <Ikona className="h-4 w-4" />}
      {children}
    </button>
  );
}

// Malé bílé tlačítko do pruhu skupiny (například tužka u cíle).
export function AkceSkupiny({ ikona: Ikona, popis, onClick, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={popis}
      aria-label={popis}
      // 14. 9. 2026 (fáze 6): pruh skupiny je nízký, tužka zůstává 28 px a na
      // telefonu má kolem sebe neviditelnou plochu 44 px (.dotyk-rozsiren).
      className="dotyk-rozsiren flex h-7 w-7 items-center justify-center rounded-lg bg-white text-brand-deep shadow-sm transition active:scale-95 disabled:opacity-50 sm:hover:bg-brand-50"
    >
      {Ikona && <Ikona className="h-3.5 w-3.5" />}
    </button>
  );
}

// Obal na řádky skupiny, tenké linky mezi nimi.
export function RadkyPrehledu({ className = "", children }) {
  return (
    <div className={`divide-y divide-slate-200 px-4 sm:px-5 ${className}`}>
      {children}
    </div>
  );
}

// Šedá pilulka s doplňkovou hodnotou.
// 13.9.2026: zaoblení na plný půlkruh, ať je stejné jako u štítků a čipů
// jinde v aplikaci. Dřív to byl zaoblený obdélník, jediný takový tvar.
export function Pilulka({ napoveda, children }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
      {children}
      {napoveda && <InfoTip text={napoveda} />}
    </span>
  );
}

// Velké číslo na jednom řádku, pod ním pilulky s doplňky. Částka se nikde neláme.
export function VelkeCislo({ hodnota, jednotka, pilulky = [] }) {
  return (
    <div className="px-4 py-4 sm:px-5">
      <div className="flex items-baseline gap-2 overflow-x-auto whitespace-nowrap">
        <span className="text-3xl font-bold leading-none tabular-nums text-slate-900 sm:text-4xl">
          {hodnota}
        </span>
        {jednotka && <span className="text-lg font-semibold text-slate-500">{jednotka}</span>}
      </div>
      {pilulky.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {pilulky.map((x, i) => (
            <Pilulka key={i} napoveda={x.napoveda}>{x.text}</Pilulka>
          ))}
        </div>
      )}
    </div>
  );
}

// Jeden řádek: popisek vlevo, číslo vpravo. Nepovinná šedá pilulka s doplňkovou
// hodnotou a nepovinný proužek s procenty (na telefonu pod řádkem, na počítači
// uprostřed řádku).
export function RadekPrehledu({
  popisek,
  napoveda,
  hodnota,
  doplnek,
  doplnekNapoveda,
  procenta,
  tonPruhu = "bg-brand",
  tonHodnoty = "text-slate-900",
  pilulky = [],
  pruh,
  barvaPruhu,
}) {
  if (procenta === undefined || procenta === null) {
    return (
      <div className="py-2.5">
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="text-slate-600">
            {popisek}
            {napoveda && <InfoTip text={napoveda} />}
          </span>
          <span className="flex shrink-0 items-center gap-2">
            {doplnek && <Pilulka napoveda={doplnekNapoveda}>{doplnek}</Pilulka>}
            <span className={`text-base font-bold tabular-nums ${tonHodnoty}`}>
              {hodnota}
            </span>
          </span>
        </div>
        {pilulky.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {pilulky.map((x, i) => (
              <Pilulka key={i} napoveda={x.napoveda}>{x.text}</Pilulka>
            ))}
          </div>
        )}
        {pruh !== undefined && pruh !== null && (
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className={`h-full rounded-full ${barvaPruhu ? "" : tonPruhu}`}
              style={{ width: `${Math.max(0, Math.min(100, Number(pruh) || 0))}%`, background: barvaPruhu }}
            />
          </div>
        )}
      </div>
    );
  }
  const sirka = Math.max(0, Math.min(100, Number(procenta) || 0));
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 py-2.5">
      <span className="flex-1 text-sm text-slate-600 sm:flex-none">
        {popisek}
        {napoveda && <InfoTip text={napoveda} />}
      </span>
      <span className="order-2 text-base font-bold tabular-nums text-slate-900 sm:order-3">
        {hodnota}
      </span>
      <span className="order-3 block h-1.5 w-full basis-full overflow-hidden rounded-full bg-slate-200 sm:order-2 sm:w-auto sm:flex-1 sm:basis-0">
        <span className={`block h-full rounded-full ${tonPruhu}`} style={{ width: `${sirka}%` }} />
      </span>
    </div>
  );
}

// Tóny malých dlaždic s čísly.
const TONY_DLAZDICE = {
  neutral: { box: "border border-slate-200 bg-slate-50", cislo: "text-slate-900", popis: "text-slate-500" },
  slate: { box: "border border-slate-200 bg-slate-50", cislo: "text-slate-700", popis: "text-slate-600" },
  zelena: { box: "bg-emerald-50", cislo: "text-emerald-700", popis: "text-emerald-800" },
  zluta: { box: "bg-amber-50", cislo: "text-amber-700", popis: "text-amber-800" },
  cervena: { box: "bg-rose-50", cislo: "text-rose-700", popis: "text-rose-800" },
  znacka: { box: "bg-brand-light", cislo: "text-brand-deep", popis: "text-brand-950" },
};

// Mřížka malých dlaždic s čísly.
export function MrizkaDlazdic({ sloupce = "grid-cols-3", className = "", children }) {
  return (
    <div className={`grid gap-2 sm:gap-2.5 ${sloupce} ${className}`}>{children}</div>
  );
}

// Malá dlaždice: velké číslo, pod ním popisek.
export function MiniDlazdice({ hodnota, popisek, napoveda, ton = "neutral", velka = false }) {
  const t = TONY_DLAZDICE[ton] || TONY_DLAZDICE.neutral;
  return (
    <div className={`h-full rounded-xl px-3 py-2.5 sm:px-4 sm:py-3 ${t.box}`}>
      <div
        className={`font-bold leading-none tabular-nums ${t.cislo} ${
          velka ? "text-2xl sm:text-3xl" : "text-xl"
        }`}
      >
        {hodnota}
      </div>
      <div className={`mt-1.5 text-xs font-medium sm:mt-2 ${t.popis}`}>
        {popisek}
        {napoveda && <InfoTip text={napoveda} />}
      </div>
    </div>
  );
}

// Zvýrazněný souhrnný řádek pod mřížkou výsledků.
const TONY_SOUHRN = {
  zluta: "border-amber-200 bg-amber-50 text-amber-900",
  cervena: "border-rose-200 bg-rose-50 text-rose-900",
  zelena: "border-emerald-200 bg-emerald-50 text-emerald-900",
  znacka: "border-brand/20 bg-brand-light text-brand-950",
};

export function SouhrnnyRadek({ popisek, napoveda, hodnota, doplnek, ton = "zluta", className = "" }) {
  return (
    <div
      className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 ${
        TONY_SOUHRN[ton] || TONY_SOUHRN.zluta
      } ${className}`}
    >
      <span className="min-w-0 text-sm font-semibold">
        {popisek}
        {napoveda && <InfoTip text={napoveda} />}
        {doplnek && (
          <span className="ml-2 inline-flex rounded-lg bg-white/70 px-2 py-0.5 text-xs font-semibold">
            {doplnek}
          </span>
        )}
      </span>
      <span className="shrink-0 whitespace-nowrap text-lg font-bold tabular-nums">{hodnota}</span>
    </div>
  );
}

// Klidné upozornění uvnitř dlaždice. Stejná rudá rodina jako jinde, bez
// animace a bez prstence, číslo zůstává největší v kartě.
export function HlaskaDlazdice({ ikona: Ikona, nadpis, hodnota, pilulky = [], text, napoveda, ton = "cervena" }) {
  const tony = {
    cervena: { box: "border-rose-200 bg-rose-50", ikona: "text-rose-700", nadpis: "text-rose-900", hodnota: "text-rose-800", pilulka: "text-rose-700", text: "text-rose-800" },
    zluta: { box: "border-amber-200 bg-amber-50", ikona: "text-amber-700", nadpis: "text-amber-900", hodnota: "text-amber-800", pilulka: "text-amber-700", text: "text-amber-800" },
  };
  const t = tony[ton] || tony.cervena;
  return (
    <div className="px-3 pb-3 sm:px-4">
      <div className={`rounded-xl border px-3 py-2.5 ${t.box}`}>
        <div className="flex items-start gap-2.5">
          {Ikona && <Ikona className={`mt-0.5 h-4 w-4 shrink-0 ${t.ikona}`} />}
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className={`text-sm font-bold ${t.nadpis}`}>{nadpis}</span>
              {hodnota && <span className={`text-lg font-bold tabular-nums ${t.hodnota}`}>{hodnota}</span>}
            </div>
            {pilulky.length > 0 && (
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {pilulky.map((x, i) => (
                  <span key={i} className={`rounded-lg bg-white px-2 py-0.5 text-xs font-semibold ${t.pilulka}`}>{x}</span>
                ))}
              </div>
            )}
            {text && (
              <p className={`mt-1.5 text-sm leading-snug ${t.text}`}>
                {text}
                {napoveda && <InfoTip text={napoveda} />}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Obal celé dlaždice.
export function KartaPrehledu({ className = "", children }) {
  return (
    <div className={`glass overflow-hidden rounded-xl ${className}`}>{children}</div>
  );
}
